import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronRight, Star, Sparkles, Trophy, Heart, Shield } from 'lucide-react';
import { authFetch } from '../lib/authFetch';

const AXIOM_LOGO = 'https://customer-assets.emergentagent.com/job_sponsor-bot-clinical/artifacts/kgiafx8p_AxiomLogo.png';

const WEEK_ICONS = [
  Shield, Star, Heart, Sparkles, Trophy, Star, Heart, Shield,
  Sparkles, Trophy, Star, Heart, Shield, Sparkles, Trophy, Star,
  Heart, Shield, Sparkles, Trophy, Star, Heart, Shield, Sparkles,
  Trophy, Star, Heart, Shield, Sparkles, Trophy, Star, Heart,
];

const WEEK_COLORS = [
  'from-blue-700 to-blue-900',
  'from-indigo-700 to-indigo-900',
  'from-emerald-700 to-emerald-900',
  'from-teal-700 to-teal-900',
  'from-cyan-700 to-cyan-900',
  'from-violet-700 to-violet-900',
  'from-amber-700 to-amber-900',
  'from-rose-700 to-rose-900',
];

function WeeklyBanner({ weekNumber, onDismiss }) {
  const [banner, setBanner] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchBanner = useCallback(async () => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/weekly-banners/${weekNumber}`);
      if (res.ok) {
        const data = await res.json();
        setBanner(data);
      }
    } catch (e) {
      console.debug('[WeeklyBanner] fetch failed:', e?.message);
    } finally {
      setLoading(false);
    }
  }, [weekNumber]);

  useEffect(() => {
    fetchBanner();
  }, [fetchBanner]);

  const handleDismiss = async () => {
    try {
      await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/week-banner-seen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ week: weekNumber }),
      });
    } catch (e) { /* non-blocking */ }
    onDismiss();
  };

  if (loading || !banner) return null;

  const colorIdx = Math.abs(weekNumber + 2) % WEEK_COLORS.length;
  const gradientClass = WEEK_COLORS[colorIdx];
  const IconComponent = WEEK_ICONS[Math.abs(weekNumber + 2) % WEEK_ICONS.length];

  return (
    <div className="fixed inset-0 z-[100] flex flex-col" data-testid="weekly-banner">
      {/* Background */}
      <div className={`absolute inset-0 bg-gradient-to-br ${gradientClass} transition-all duration-500`} />

      {/* Dismiss button */}
      <button
        onClick={handleDismiss}
        className="absolute top-4 right-4 z-10 text-white/60 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
        data-testid="weekly-banner-dismiss"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Content */}
      <div className="relative flex-1 flex flex-col items-center justify-center px-6 max-w-lg mx-auto w-full">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/20">
            <img src={AXIOM_LOGO} alt="Axiom" className="h-12" />
          </div>
        </div>

        {/* Week Label */}
        <p className="text-center text-white/60 text-xs font-bold uppercase tracking-[0.2em] mb-2">
          {banner.week_label}
        </p>

        {/* Title */}
        <h1 className="text-center text-white text-3xl sm:text-4xl font-bold mb-4 leading-tight">
          {banner.title}
        </h1>

        {/* Congratulations Message */}
        <p className="text-center text-white/90 text-base sm:text-lg leading-relaxed max-w-sm mx-auto mb-6">
          {banner.message}
        </p>

        {/* TSF Quote */}
        {banner.tsf_message && (
          <div className="bg-white/10 border border-white/20 rounded-xl px-5 py-4 max-w-sm mx-auto backdrop-blur-sm">
            <p className="text-center text-white/80 text-sm italic leading-relaxed">
              "{banner.tsf_message}"
            </p>
          </div>
        )}
      </div>

      {/* Bottom CTA */}
      <div className="relative pb-10 px-6">
        <div className="flex justify-center">
          <button
            onClick={handleDismiss}
            className="flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-zinc-900 text-sm font-bold shadow-lg shadow-black/20 hover:bg-white/90 transition-all"
            data-testid="weekly-banner-continue"
          >
            Continue to Dashboard
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Axiom branding */}
        <div className="flex justify-center mt-4">
          <img src={AXIOM_LOGO} alt="Axiom" className="h-6 opacity-40" />
        </div>
      </div>
    </div>
  );
}

export default WeeklyBanner;
